import { useCallback, useEffect, useState } from 'react';
import { legacy } from '@/shared/api/client';
import type { Store } from '@/shared/types/store';

export function useStores(idPassbook: string, enabled = true) {
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!idPassbook || !enabled) {
      setStores([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    legacy
      .get<Store[]>(`/stores/${idPassbook}`, { headers: { 'X-Passbook-Id': idPassbook } })
      .then((data) => {
        if (!cancelled) setStores(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idPassbook, enabled]);

  const resolveStoreName = useCallback(
    (codeOrName: string): string => {
      const match = stores.find(
        (s) =>
          s.storeCod === codeOrName ||
          s.storeName === codeOrName ||
          s._id === codeOrName ||
          String(s.branchId ?? '') === codeOrName,
      );
      return match?.storeName || codeOrName;
    },
    [stores],
  );

  const resolveLeadStores = useCallback(
    (lead: { stores?: string[]; storeName?: string; location?: string }): string[] => {
      if (!lead.stores?.length && !lead.storeName && lead.location) {
        return [lead.location];
      }
      const codes = lead.stores?.length ? lead.stores : lead.storeName ? [lead.storeName] : [];
      const unique = [...new Set(codes.filter(Boolean))];
      return unique.map(resolveStoreName);
    },
    [resolveStoreName],
  );

  return { stores, isLoading, resolveStoreName, resolveLeadStores };
}
